/*
  Drill-down behaviour for the mobile menu drawer (snippets/side-menu.liquid).

  Every submenu is a `.mm-panel` — a flat sibling inside `.mm-viewport`, parked off-screen at
  translateX(100%). Opening one adds `.is-open` and the CSS slides it in over whatever is behind.
  Panels are siblings rather than nested because an absolutely positioned element scrolls with its
  positioned ancestor: a panel nested inside the root list would open offset by however far that
  list had been scrolled. See the comment above the second pass in side-menu.liquid.

  Depth (data-mm-depth: 1 for a top-level submenu, 2 for the level under it) replaces the nesting
  the DOM no longer has. "Back" closes the deepest open panel; opening a panel closes anything at
  the same depth or below, which is what makes tapping a second sibling replace the first.

  All state is read from the DOM on each interaction rather than held in a variable. The header is
  a Shopify section: the theme editor replaces the whole node on `shopify:section:load`, and a
  cached stack of element references would point at detached nodes the moment it does. Every
  listener is delegated off `document` for the same reason.

  Deliberately vanilla and standalone. assets/common.js owns the drawer's open/close (it adds
  `nav-open` to the body) and still owns the old accordion, which the catalog tab in the same
  drawer continues to use — see the comment on that tab in side-menu.liquid.
*/
(function () {
  'use strict';

  var DRAWER = '#mobileMenu';

  /* Only used if the panel cannot be measured — see panelTransitionMs below. Matches --dur-slow,
     which is what .mm-panel is currently transitioned at. */
  var TRANSITION_FALLBACK_MS = 320;

  /* How long to wait after the drawer starts closing before rewinding the panels behind it.

     This used to be a hardcoded 340 mirroring the .mm-panel transition in mobile-menu.css, with a
     comment asking the next person to keep the two in step. That is a contract no one can see
     from the CSS side: retiming the panel there left this silently wrong, and the failure is
     subtle enough to survive review — the panels rewind a fraction early, while the drawer is
     still on screen, which reads as a flicker rather than as a bug.

     Reading the value the browser actually resolved removes the coupling entirely, and gets the
     reduced-motion case right for free: when the transition is switched off the computed duration
     is 0s, so the reset happens on the next tick instead of after a wait for motion that never
     ran. */
  function panelTransitionMs(drawer) {
    var panel = drawer && drawer.querySelector('.mm-panel');
    if (!panel || typeof window.getComputedStyle !== 'function') return TRANSITION_FALLBACK_MS;

    var declared = window.getComputedStyle(panel).transitionDuration;
    if (!declared) return TRANSITION_FALLBACK_MS;

    /* Comma-separated when several properties are transitioned; the first is transform, which is
       the one being waited on. */
    var seconds = parseFloat(declared);
    if (isNaN(seconds) || seconds < 0) return TRANSITION_FALLBACK_MS;
    return seconds * 1000;
  }

  function drawerFor(node) {
    return node && node.closest ? node.closest(DRAWER) : null;
  }

  function depthOf(panel) {
    return parseInt(panel.getAttribute('data-mm-depth'), 10) || 0;
  }

  function openPanels(drawer) {
    return Array.prototype.slice.call(drawer.querySelectorAll('.mm-panel.is-open'));
  }

  /* Only one branch is ever open at a time, so the greatest depth is unambiguously the top. */
  function deepestOpen(drawer) {
    return openPanels(drawer).reduce(function (deepest, panel) {
      return !deepest || depthOf(panel) > depthOf(deepest) ? panel : deepest;
    }, null);
  }

  function triggerFor(drawer, panel) {
    return drawer.querySelector('[data-mm-open="' + panel.id + '"]');
  }

  /* Whatever the user is looking at: the deepest open panel, or the root when none is open. */
  function visiblePanel(drawer) {
    return deepestOpen(drawer) || drawer.querySelector('.mm-panel--root');
  }

  /*
    Edge-fade state for the gradients in mobile-menu.css — the same is-scrollable / at-top /
    at-bottom contract the PDP thumbnail rail and size rows use. Measuring is what makes the fade
    honest: a permanent bottom fade on a list that already fits promises content that is not there.

    The classes go on the viewport, not the panel, because that is where the overlay lives — see
    the comment on those rules. Only the visible panel is measured; the others are re-measured as
    they open.

    The 1px slack absorbs sub-pixel scroll positions, which fractional device pixel ratios and
    momentum scrolling both produce — without it a panel scrolled fully to the bottom can report a
    remainder of half a pixel and keep fading an edge with nothing past it.
  */
  function updateFade(drawer) {
    if (!drawer) return;
    var viewport = drawer.querySelector('.mm-viewport');
    var panel = visiblePanel(drawer);
    if (!viewport || !panel) return;

    var overflow = panel.scrollHeight - panel.clientHeight;
    viewport.classList.toggle('is-scrollable', overflow > 1);
    viewport.classList.toggle('at-top', panel.scrollTop <= 1);
    viewport.classList.toggle('at-bottom', panel.scrollTop >= overflow - 1);
  }

  /* updateFade reads scrollHeight, clientHeight and scrollTop. Those are geometry reads, so calling
     it while a style invalidation is pending makes the browser flush a full style recalc and layout
     synchronously, right there in the handler. Two call sites cannot afford that:

     - The hamburger tap. assets/common.js:3055 has just put `nav-open` on <body>, which invalidates
       style for the WHOLE document: body overflow changes, a fixed scrim appears, and every
       `body.nav-open #mobileMenu ...` stagger selector starts matching. Reading geometry in the same
       task forces all of that to be resolved before the first frame of the drawer slide is ever
       composited. On an A13-class iPhone that lands as a visible hitch at the head of the cascade.
     - Scroll, where the unthrottled version ran a read-write-read per event for the whole of an iOS
       momentum scroll.

     Deferring to rAF fixes both: the read happens after the browser's own style flush instead of
     forcing one, and repeat calls in a frame collapse into one. Same shape as measureOpen() in
     assets/mega-menu.js. The fade is two cosmetic 24px gradients — a frame late is not observable,
     which is what makes this safe to use on every path except the initial measurement. */
  var fadeFrame = null;
  var fadeTarget = null;

  function scheduleFade(drawer) {
    if (!drawer) return;
    /* Last caller wins rather than first: there is only ever one drawer, but if that changes the
       newer target is the correct one to measure. */
    fadeTarget = drawer;
    if (fadeFrame) return;
    fadeFrame = window.requestAnimationFrame(function () {
      fadeFrame = null;
      updateFade(fadeTarget);
    });
  }

  function closePanel(drawer, panel) {
    panel.classList.remove('is-open');
    var trigger = triggerFor(drawer, panel);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  /* The topbar is the Back button and the close button; Back is the only part that changes. */
  function syncTopbar(drawer) {
    var back = drawer.querySelector('[data-mm-back]');
    if (back) back.hidden = !deepestOpen(drawer);
  }

  function openPanel(trigger) {
    var drawer = drawerFor(trigger);
    if (!drawer) return;

    var panel = drawer.querySelector('#' + trigger.getAttribute('data-mm-open'));
    if (!panel) return;

    /* Switching to a sibling, or jumping back up a level, means everything from this depth
       downwards has to go — otherwise the one we are leaving stays stacked underneath. */
    var depth = depthOf(panel);
    openPanels(drawer).forEach(function (open) {
      if (depthOf(open) >= depth) closePanel(drawer, open);
    });

    panel.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.scrollTop = 0;
    syncTopbar(drawer);
    scheduleFade(drawer);
  }

  function goBack(drawer) {
    if (!drawer) return;
    var panel = deepestOpen(drawer);
    if (panel) closePanel(drawer, panel);
    syncTopbar(drawer);
    scheduleFade(drawer);
  }

  function reset(drawer) {
    if (!drawer) return;
    openPanels(drawer).forEach(function (panel) {
      closePanel(drawer, panel);
    });
    var root = drawer.querySelector('.mm-panel--root');
    if (root) root.scrollTop = 0;
    syncTopbar(drawer);
    scheduleFade(drawer);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var trigger = target.closest('[data-mm-open]');
    if (trigger) {
      event.preventDefault();
      openPanel(trigger);
      return;
    }

    if (target.closest('[data-mm-back]')) {
      event.preventDefault();
      goBack(drawerFor(target));
      return;
    }

    /* Closing the drawer: let it slide out first, then rewind to the root behind it. Resetting
       immediately would play the panels sliding back across the still-visible drawer. */
    if (target.closest(DRAWER + ' .close-btn')) {
      var closing = drawerFor(target);
      window.setTimeout(function () {
        reset(closing);
      }, panelTransitionMs(closing));
      return;
    }

    /* Reopening from the hamburger always starts at the root. common.js handles the open itself;
       this only rewinds the panels, which is a no-op in the normal case. */
    if (target.closest('.navbar-toggler')) {
      reset(document.querySelector(DRAWER));
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    if (!document.body.classList.contains('nav-open')) return;
    var drawer = document.querySelector(DRAWER);
    /* Inside a panel, Escape steps back one level rather than closing the whole drawer. */
    if (drawer && deepestOpen(drawer)) {
      event.stopPropagation();
      goBack(drawer);
    }
  });

  /* Scroll does not bubble, so this listens on the capture phase instead of delegating normally.

     `{ capture: true, passive: true }` rather than the bare `true` this used to pass. Scroll events
     are not cancelable so passive changes nothing functionally, but this was the only listener in
     the theme not declaring it — every other one does, and sections/header.liquid:452 carries the
     comment explaining why. Declaring it keeps the convention honest and costs nothing. */
  document.addEventListener(
    'scroll',
    function (event) {
      var panel = event.target;
      if (panel && panel.classList && panel.classList.contains('mm-panel')) {
        scheduleFade(drawerFor(panel));
      }
    },
    { capture: true, passive: true }
  );

  /* Rotating the phone or opening the on-screen keyboard changes the panel height, which can turn
     a list that overflowed into one that fits, or the other way round.

     iOS also fires resize every time the URL bar collapses or expands during a scroll, which is far
     more often than either of those. That is why this goes through scheduleFade: unthrottled it was
     forcing a layout mid-gesture. */
  window.addEventListener('resize', function () {
    scheduleFade(document.querySelector(DRAWER));
  });

  document.addEventListener('shopify:section:load', function (event) {
    var drawer = event.target.querySelector ? event.target.querySelector(DRAWER) : null;
    if (!drawer) return;
    syncTopbar(drawer);
    scheduleFade(drawer);
  });

  /* The drawer is off-screen at load, not display:none, so it is laid out and measurable straight
     away — the root panel's fade is correct before the hamburger is ever tapped. Images in the
     rows can still change its height as they arrive, hence the load listener as well. */
  updateFade(document.querySelector(DRAWER));
  window.addEventListener('load', function () {
    updateFade(document.querySelector(DRAWER));
  });
})();
